import { Avatar, useTheme } from "@mui/material";
import { useMemo } from "react";
import { LocalCalendarBlockBase, LocalShiftBase, LocalVisit, LocalVisitResource, LocalVisitResourceBase } from "~/hooks/visitDragDropReducer";
import { CommandIndexCareRequirementVisitsQuery, CommandIndexProvidersQuery, VisitEventStatusEnum } from "~/graphql/__generated__/graphql";
import { CustomMbscCalendarEventData, adjustVisitTimesForLabDropOff, getEventCalendarOrder, LocalVisitRequest } from "~/utils/eventsHelper";
import { FilterState, filterVisits } from "./filters";
import { OrgConfig } from "~/providers/OrgConfigProvider";
import { buildCareRequirementWarningsByVisitResourceId, getVisitWarning } from "./visitWarningUtils";
import { UNASSIGNED_PROVIDER_ID } from "~/utils/visitsHelper";
import { visitResourceStatus } from "~/utils/statuses";

type PatientResource = {
  id: number | string;
  name: string;
};

type ActivePatientSummary = {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
};

export type PatientMobiscrollResult = {
  visitEvents: CustomMbscCalendarEventData[];
  calendarResources: PatientResource[];
};

const visitsToCalendarEvents = (
  visibleVisits: LocalVisitResourceBase,
  lastAddedVisitId: number | null,
  providerLookup: Record<number, CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]>,
  localShifts: LocalShiftBase,
  localVisitResources: LocalVisitResource[],
  localVisits: LocalVisit[],
  localVisitRequests: LocalVisitRequest[],
  localCalendarBlocks: LocalCalendarBlockBase,
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"],
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  orgConfig: OrgConfig,
): CustomMbscCalendarEventData[] => {
  const careRequirementWarningsByVisitResourceId = buildCareRequirementWarningsByVisitResourceId(
    localVisitResources,
    careRequirementVisits
  );
  const baseEvents = visibleVisits.map((vr) => {
    const patientId = vr.visit.patient?.id;
    const providerFromLookup = providerLookup[vr.providerId ?? UNASSIGNED_PROVIDER_ID];
    const providerName = providerFromLookup
      ? `${providerFromLookup.firstName ?? ""} ${providerFromLookup.lastName ?? ""}`.trim()
      : vr.provider
        ? `${vr.provider.firstName ?? ""} ${vr.provider.lastName ?? ""}`.trim()
        : "";

    const cssClass = vr.visit.id === lastAddedVisitId ? "blink" : "";

    const { startTime, endTime, bufferBefore } = adjustVisitTimesForLabDropOff(vr, orgConfig);

    const event: CustomMbscCalendarEventData = {
      id: vr.id,
      referenceId: vr.id,
      start: startTime,
      end: endTime,
      date: new Date(startTime).getTime(),
      startDate: new Date(startTime),
      endDate: new Date(endTime),
      dbDate: vr.visit.startTime,
      resource: patientId,
      providerId: vr.providerId ?? null,
      title: providerName,
      type: "visit_resource" as const,
      order: getEventCalendarOrder("visit_resource"),
      cssClass,
      bufferBefore: bufferBefore ?? "",
      dragBetweenResources: false,
      dragInTime: false,
    };

    return {
      ...event,
      tooltip: getVisitWarning(
        event,
        localShifts,
        localVisitResources,
        localVisits,
        localVisitRequests,
        localCalendarBlocks,
        providers,
        orgConfig,
        {},
        careRequirementWarningsByVisitResourceId
      ),
    };
  });

  return baseEvents;
};

const formatPatientName = (firstName?: string | null, lastName?: string | null, fallbackId?: number): string => {
  const last = lastName?.trim() ?? "";
  const first = firstName?.trim() ?? "";
  if (last && first) return `${last}, ${first}`;
  if (last) return last;
  if (first) return first;
  return fallbackId ? `Patient ${fallbackId}` : "Unknown";
};

const patientsToCalendarResources = (
  visibleVisits: LocalVisitResourceBase,
  activePatients: ActivePatientSummary[],
  showAllPatients: boolean,
): PatientResource[] => {
  const patients: Record<number, PatientResource> = {};
  const scheduledPatientIds = new Set<number>();

  visibleVisits.forEach((vr) => {
    const patient = vr.visit.patient;
    if (!patient?.id) {
      return;
    }
    if (visitResourceStatus(vr) !== VisitEventStatusEnum.Canceled) {
      scheduledPatientIds.add(patient.id);
    }

    if (!patients[patient.id]) {
      const name = formatPatientName(patient.firstName, patient.lastName, patient.id);
      patients[patient.id] = {
        id: patient.id,
        name,
      };
    }
  });

  // Separate patients into scheduled (has non-canceled visits) and canceled-only
  const scheduledResources: PatientResource[] = [];
  const canceledOnlyResources: PatientResource[] = [];
  
  Object.values(patients).forEach((patient) => {
    const patientId = typeof patient.id === "number" ? patient.id : parseInt(`${patient.id}`, 10);
    if (scheduledPatientIds.has(patientId)) {
      scheduledResources.push(patient);
    } else {
      canceledOnlyResources.push(patient);
    }
  });

  scheduledResources.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  canceledOnlyResources.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  if (!showAllPatients) {
    return [...scheduledResources, ...canceledOnlyResources];
  }

  const additionalResources: PatientResource[] = activePatients
    .filter((patient) => Number.isFinite(patient.id) && !patients[patient.id])
    .map((patient) => {
      const name = formatPatientName(patient.firstName, patient.lastName, patient.id);
      return {
        id: patient.id,
        name,
      };
    });

  return [...scheduledResources, ...canceledOnlyResources, ...additionalResources];
};

type UsePatientMobiscrollOptions = {
  loading: boolean;
  visitResources: LocalVisitResourceBase;
  lastAddedVisitId: number | null;
  filters: FilterState;
  currentProviderId: number | null;
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  localShifts: LocalShiftBase;
  localVisits: LocalVisit[];
  localVisitRequests: LocalVisitRequest[],
  localCalendarBlocks: LocalCalendarBlockBase;
  orgConfig: OrgConfig;
  activePatients: ActivePatientSummary[];
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"];
};

const buildPatientMobiscrollData = (
  visitResources: LocalVisitResourceBase,
  lastAddedVisitId: number | null,
  filters: FilterState,
  currentProviderId: number | null,
  providerLookup: Record<number, CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]>,
  localShifts: LocalShiftBase,
  localVisitResources: LocalVisitResource[],
  localVisits: LocalVisit[],
  localVisitRequests: LocalVisitRequest[],
  localCalendarBlocks: LocalCalendarBlockBase,
  orgConfig: OrgConfig,
  activePatients: ActivePatientSummary[],
  careRequirementVisits: CommandIndexCareRequirementVisitsQuery["visit"],
): PatientMobiscrollResult => {
  const visibleVisits = filterVisits(visitResources, filters, currentProviderId);

  const visitEvents = visitsToCalendarEvents(
    visibleVisits,
    lastAddedVisitId,
    providerLookup,
    localShifts,
    localVisitResources,
    localVisits,
    localVisitRequests,
    localCalendarBlocks,
    careRequirementVisits,
    Object.values(providerLookup),
    orgConfig,
  );

  const calendarResources = patientsToCalendarResources(visibleVisits, activePatients, filters.showAllPatients);

  return {
    visitEvents,
    calendarResources,
  };
};

const useMobiscrollPatientCalendarEvents = ({
  loading,
  visitResources,
  lastAddedVisitId,
  filters,
  currentProviderId,
  providers,
  localShifts,
  localVisits,
  localVisitRequests,
  localCalendarBlocks,
  orgConfig,
  activePatients,
  careRequirementVisits,
}: UsePatientMobiscrollOptions): PatientMobiscrollResult => {
  const providerLookup = useMemo(() => {
    return Object.fromEntries(
      (providers ?? []).map((provider) => [provider.id, provider])
    ) as Record<number, CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]>;
  }, [providers]);

  const theme = useTheme();
  return useMemo(() => {
    if (loading) {
      return {
        visitEvents: [],
        calendarResources: [
          {
            id: "loading",
            name: "Loading...",
            avatar: <Avatar src="" alt="Loading" sx={{ bgcolor: theme.palette.primary.light }} />,
          },
        ],
      };
    }

    const data = buildPatientMobiscrollData(
      visitResources,
      lastAddedVisitId,
      filters,
      currentProviderId,
      providerLookup,
      localShifts,
      visitResources,
      localVisits,
      localVisitRequests,
      localCalendarBlocks,
      orgConfig,
      activePatients,
      careRequirementVisits,
    );
    return data;
  }, [loading, visitResources, lastAddedVisitId, filters, currentProviderId, providerLookup, localShifts, localVisits, localVisitRequests, localCalendarBlocks, orgConfig, activePatients, careRequirementVisits, theme]);
};

export default useMobiscrollPatientCalendarEvents;
